import React from 'react';
import { X, History, Clock, Tag, FileText, ArrowRight } from 'lucide-react';

export default function HistoryLogModal({ isOpen, onClose, logs, protocolName }) {
  if (!isOpen) return null;

  const getActionBadge = (actionType) => {
    switch (actionType) {
      case 'CREATE':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            CREATE
          </span>
        );
      case 'UPDATE':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20">
            UPDATE
          </span>
        );
      case 'STATUS_CHANGE':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
            STATUS CHANGE
          </span>
        );
      case 'DELETE':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/20">
            DELETE
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-400">
            {actionType}
          </span>
        );
    }
  };

  const formatTimestamp = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts || 'Unknown';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-950/80 backdrop-blur-sm overflow-hidden animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full flex flex-col max-h-[92vh] sm:max-h-[88vh] p-5 sm:p-6 shadow-2xl relative my-auto overflow-hidden">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-4 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {protocolName ? `Audit Log: ${protocolName}` : 'Global Protocol History Log'}
              </h2>
              <p className="text-xs text-slate-400">
                Detailed timeline of protocol modifications, status changes, and corrections
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Logs Timeline */}
        {logs.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-xs">
            No audit logs found for this protocol.
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {logs.map((log) => (
              <div 
                key={log.id} 
                className="p-4 rounded-xl bg-slate-950 border border-slate-800/80 relative"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getActionBadge(log.actionType)}
                    <span className="text-xs font-mono text-slate-400 flex items-center">
                      <Clock className="w-3 h-3 mr-1 text-slate-500" />
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>
                </div>

                {/* Changed Fields list */}
                {log.changedFields && Object.keys(log.changedFields).length > 0 && (
                  <div className="my-2 space-y-1 bg-slate-900/60 p-2.5 rounded-lg border border-slate-800 text-xs">
                    {Object.entries(log.changedFields).map(([key, change]) => (
                      <div key={key} className="flex items-center space-x-2 text-slate-300">
                        <Tag className="w-3 h-3 text-sky-400 flex-shrink-0" />
                        <span className="font-semibold text-slate-300 capitalize">{key}:</span>
                        <span className="text-red-400 line-through truncate max-w-[120px]">
                          {String(change.old ?? 'None')}
                        </span>
                        <ArrowRight className="w-3 h-3 text-slate-500 flex-shrink-0" />
                        <span className="text-emerald-400 font-medium truncate max-w-[150px]">
                          {String(change.new ?? 'None')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Log Notes */}
                {log.notes && (
                  <div className="text-xs text-slate-400 flex items-start space-x-1.5 mt-2 italic">
                    <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
                    <span>"{log.notes}"</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 border-t border-slate-800 mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium transition"
          >
            Close History
          </button>
        </div>

      </div>
    </div>
  );
}
